import React from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getPlanetAbbr } from '../../i18n/astrology';

const TRANSIT_WHEEL_PLANETS = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];
const IMPORTANT_TRANSIT_PLANETS = new Set(['JUPITER', 'SATURN', 'RAHU', 'KETU']);
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const PLANET_COLORS: Record<string, string> = {
  SUN: '#dc2626',
  MOON: '#8b5cf6',
  MARS: '#ef4444',
  MERCURY: '#16a34a',
  JUPITER: '#d97706',
  VENUS: '#0f766e',
  SATURN: '#4f46e5',
  RAHU: '#111827',
  KETU: '#92400e',
};

const polarPoint = (cx: number, cy: number, radius: number, angleDeg: number) => {
  const radians = (angleDeg - 90) * Math.PI / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
};

const getTransitPlanetEntries = (transitChart: any) => (
  TRANSIT_WHEEL_PLANETS
    .map((key) => {
      const planet = transitChart?.planets?.[key];
      if (!planet || typeof planet.longitude !== 'number') return null;
      const longitude = ((planet.longitude % 360) + 360) % 360;
      const signNumber = planet.sign?.number || Math.floor(longitude / 30) + 1;
      return {
        key,
        planet,
        longitude,
        signNumber,
        degreeInSign: longitude % 30,
        angle: longitude,
        color: PLANET_COLORS[key] || '#1f2937',
        isImportant: IMPORTANT_TRANSIT_PLANETS.has(key),
      };
    })
    .filter(Boolean)
);

interface TransitWheelProps {
  transitChart: any;
  loading?: boolean;
  error?: string | null;
  size?: number;
  title?: string;
}

const TransitWheel: React.FC<TransitWheelProps> = ({
  transitChart,
  loading = false,
  error = null,
  size = 360,
  title,
}) => {
  const { t, i18n } = useTranslation();
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.45;
  const signR = size * 0.28;
  const planetR = size * 0.43;
  const tickOuter = size * 0.46;
  const tickInnerShort = size * 0.44;
  const tickInnerLong = size * 0.41;
  const entries = getTransitPlanetEntries(transitChart);
  const isHindi = Boolean(i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi'));
  const calculatedAt = transitChart?.calculationData?.calculatedAt
    ? new Date(transitChart.calculationData.calculatedAt)
    : new Date();
  const dateLabel = calculatedAt.toLocaleDateString(isHindi ? 'hi-IN' : 'en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timeLabel = calculatedAt.toLocaleTimeString(isHindi ? 'hi-IN' : 'en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  if (loading) {
    return (
      <Box sx={{ border: '1px solid rgba(46,125,80,0.28)', borderRadius: 2, p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('astrology.loadingCurrentTransit')}
        </Typography>
      </Box>
    );
  }

  if (error || !transitChart) {
    return (
      <Box sx={{ border: '1px solid #fca5a5', borderRadius: 2, p: 2 }}>
        <Typography color="error" fontWeight={600}>{error || 'Transit wheel unavailable.'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid rgba(46,125,80,0.28)', borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
      <Typography variant="h6" sx={{ color: '#2e7d52', fontWeight: 600, mb: 1 }}>
        {title || `${t('nav.currentTransit')} Wheel`}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Transit wheel">
          <circle cx={cx} cy={cy} r={outerR} fill="#f8fafc" stroke="#dc2626" strokeWidth={1.4} />
          <circle cx={cx} cy={cy} r={signR * 0.62} fill="#fff7ed" stroke="#dc2626" strokeWidth={1.2} />
          <circle cx={cx} cy={cy} r={outerR * 0.70} fill="none" stroke="#9ca3af" strokeWidth={0.8} strokeDasharray="2 3" />
          {Array.from({ length: 360 }, (_, degree) => {
            const major = degree % 30 === 0;
            const medium = degree % 10 === 0;
            const p1 = polarPoint(cx, cy, major ? tickInnerLong : tickInnerShort, degree);
            const p2 = polarPoint(cx, cy, tickOuter, degree);
            return (
              <line
                key={`tick-${degree}`}
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={major ? '#dc2626' : medium ? '#16a34a' : '#ef4444'}
                strokeWidth={major ? 1.3 : medium ? 1 : 0.55}
              />
            );
          })}
          {Array.from({ length: 12 }, (_, index) => {
            const angle = index * 30;
            const p1 = polarPoint(cx, cy, outerR * 0.70, angle);
            const p2 = polarPoint(cx, cy, outerR, angle);
            const label = polarPoint(cx, cy, signR, angle + 15);
            const roman = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'][index];
            return (
              <g key={`sign-${index}`}>
                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#b91c1c" strokeWidth={1.1} />
                <text x={label.x} y={label.y - 7} textAnchor="middle" fontSize={9} fill="#2f855a" fontWeight={600}>
                  {roman}
                </text>
                <text x={label.x} y={label.y + 10} textAnchor="middle" fontSize={16} fill="#16a34a" fontWeight={600}>
                  {index + 1}
                </text>
              </g>
            );
          })}
          {entries.map((entry: any, index: number) => {
            const clusterOffset = (index % 3 - 1) * 10;
            const point = polarPoint(cx, cy, planetR + clusterOffset, entry.angle);
            const abbr = getPlanetAbbr(t, entry.key);
            return (
              <g key={entry.key}>
                <circle cx={point.x} cy={point.y - 4} r={entry.isImportant ? 11 : 9} fill="#fff" stroke={entry.color} strokeWidth={entry.isImportant ? 1.7 : 1} opacity={0.95} />
                <text x={point.x} y={point.y} textAnchor="middle" fontSize={12} fill={entry.color} fontWeight={entry.isImportant ? 900 : 700}>
                  {abbr}
                </text>
              </g>
            );
          })}
          <text x={cx} y={cy - 24} textAnchor="middle" fontSize={20} fill="#b91c1c" fontWeight={600}>As</text>
          <text x={cx} y={cy + 2} textAnchor="middle" fontSize={15} fill="#b91c1c" fontWeight={600}>{dateLabel}</text>
          <text x={cx} y={cy + 22} textAnchor="middle" fontSize={14} fill="#b91c1c" fontWeight={600}>{timeLabel}</text>
        </svg>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
        {entries.map((entry: any) => (
          <Chip
            key={`legend-${entry.key}`}
            size="small"
            variant={entry.isImportant ? 'filled' : 'outlined'}
            label={`${getPlanetAbbr(t, entry.key)} ${SIGNS[entry.signNumber - 1]} ${entry.degreeInSign.toFixed(1)}°${entry.planet.isRetrograde ? ' R' : ''}`}
            sx={{
              color: entry.isImportant ? '#fff' : entry.color,
              borderColor: entry.color,
              bgcolor: entry.isImportant ? entry.color : 'transparent',
              fontWeight: 600,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TransitWheel;
